import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, formatApiError } from "../lib/format";
import { PAYMENT_TYPES, INVOICE_STATUS_LABEL } from "../lib/constants";

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ booking_id: "", invoice_date: new Date().toISOString().slice(0,10), due_date: "", payment_type: "DP 1", amount: 0, notes: "" });

  const load = () => api.get("/invoices").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/bookings").then(({ data }) => setBookings(data));
  }, []);

  const submit = async () => {
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Invoice diterbitkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Invoice Klien</h1>
          <p className="text-sm text-[#5C6066] mt-1">Nomor: <span className="font-mono">INV-DWG-YYYY-XXXX</span></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand text-white" data-testid="btn-add-invoice"><Plus className="w-4 h-4 mr-1.5" /> Invoice Baru</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Buat Invoice</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Pemesanan</Label>
                <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="inv-booking-select"><SelectValue placeholder="Pilih pemesanan" /></SelectTrigger>
                  <SelectContent>
                    {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_code} — {b.event_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tgl Invoice</Label><Input type="date" className="mt-1.5" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
                <div><Label>Jatuh Tempo</Label><Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              </div>
              <div><Label>Termin</Label>
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nominal (Rp)</Label><Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="inv-amount" /></div>
              <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-brand text-white" onClick={submit} disabled={!form.booking_id || !form.amount} data-testid="btn-save-invoice">Terbitkan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Nomor</th>
                <th className="text-left px-4 py-3">Klien / Pemesanan</th>
                <th className="text-left px-4 py-3">Tgl Invoice</th>
                <th className="text-left px-4 py-3">Jatuh Tempo</th>
                <th className="text-right px-4 py-3">Nominal</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Belum ada invoice.</td></tr>}
              {rows.map((i) => (
                <tr key={i.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`invoice-row-${i.invoice_code}`}>
                  <td className="px-5 py-3"><Link to={`/invoices/${i.id}`} className="font-mono text-xs text-brand hover:underline">{i.invoice_code}</Link></td>
                  <td className="px-4 py-3">
                    <div>{i.client_name}</div>
                    <div className="text-[11px] text-[#8C9096] font-mono">{i.booking_code}</div>
                  </td>
                  <td className="px-4 py-3">{formatDate(i.invoice_date)}</td>
                  <td className="px-4 py-3">{formatDate(i.due_date)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatIDR(i.amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status} label={INVOICE_STATUS_LABEL[i.status]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-[#F0EBE0]">
          {rows.map((i) => (
            <Link key={i.id} to={`/invoices/${i.id}`} className="block p-4 hover:bg-[#F7F4EE]">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-mono text-xs text-brand">{i.invoice_code}</div>
                  <div className="font-medium mt-1">{i.client_name}</div>
                  <div className="text-[11px] text-[#8C9096]">Jatuh tempo {formatDate(i.due_date)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold">{formatIDR(i.amount)}</div>
                  <StatusBadge status={i.status} label={INVOICE_STATUS_LABEL[i.status]} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
